import {
  Flex,
  Icon,
  Text,
  getToken,
  ColorsV2,
  BoxProps,
  spaces,
  Chip,
  useThemeColors,
  Divider,
} from '@electricjs/arc';
import { FilterDropdown } from '@common';
import styled from 'styled-components';
import { FilterOption } from 'common/TableFilters/types';
import { useState } from 'react';

type SelectFilterProps<T> = {
  filterName: string;
  selectedValues: T[];
  options: FilterOption<T>[];
  handleAddFilter: (value: T) => void;
  dropdownProps?: BoxProps;
  showSelectedValuesChips?: boolean;
};

export const StyledFilterOption = styled(Flex)<{ disabled?: boolean }>`
  &:hover {
    background-color: ${props =>
      props.disabled ? 'inherit' : getToken(ColorsV2.PRIMARY_LIGHTEST)};
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  }
  opacity: ${props => (props.disabled ? 0.5 : 1)};
`;

const SelectFilter = <T extends string>({
  filterName,
  selectedValues = [],
  options,
  handleAddFilter,
  dropdownProps,
  showSelectedValuesChips = false,
}: SelectFilterProps<T>) => {
  const someOptionIsChecked = selectedValues.length > 0;
  const [chipBgColor] = useThemeColors([
    ColorsV2.BACKGROUND_ALT,
    ColorsV2.PRIMARY,
  ]);
  const [dropdownKey, setDropdownKey] = useState(0);

  const { space_l, space_m, space_s, space_xs, space_2xs } = spaces;
  const selectedValuesDisplay =
    showSelectedValuesChips && selectedValues.length > 0
      ? `: ${selectedValues
          .map(value => options.find(option => option.value === value)?.text)
          .join(', ')}`
      : '';

  const handleOptionClick = (value: T) => {
    if (!options.find(opt => opt.value === value)?.disabled) {
      handleAddFilter(value);
      // Force dropdown to close by changing the key
      setDropdownKey(prev => prev + 1);
    }
  };

  return (
    <FilterDropdown
      key={dropdownKey}
      someOptionIsChecked={someOptionIsChecked}
      dropdownContent={
        <>
          {showSelectedValuesChips && selectedValues.length > 0 && (
            <Flex flexWrap="wrap" gap={space_xs} p={space_m}>
              {selectedValues.map(value => {
                return (
                  <Chip
                    key={value}
                    id={`selected-value-${value}`}
                    backgroundColor={chipBgColor}
                    onRemove={() => handleAddFilter(value)}>
                    {options.find(option => option.value === value)?.text}
                  </Chip>
                );
              })}
            </Flex>
          )}
          <Divider />
          {options.map(option => {
            const showIcon = selectedValues.includes(option.value);
            const hasSubtext = !!option.subtext;

            return (
              <StyledFilterOption
                id={`filter-${option.id}`}
                key={option.id}
                onClick={() => handleOptionClick(option.value)}
                disabled={option.disabled}
                p={`${space_xs} ${space_s}`}
                alignItems={hasSubtext ? 'flex-start' : 'center'}>
                {showIcon && (
                  <Icon
                    icon="check"
                    intent="primary"
                    size="xsmall"
                    mt={hasSubtext ? space_2xs : '0'}
                  />
                )}
                <Flex flexDirection="column">
                  <Text
                    variant={hasSubtext ? 'body' : 'body-2'}
                    pl={someOptionIsChecked && !showIcon ? space_l : space_s}>
                    {option.text}
                  </Text>
                  {hasSubtext && (
                    <Text
                      variant="body-2"
                      intent="secondary"
                      pl={someOptionIsChecked && !showIcon ? space_l : space_s}>
                      {option.subtext}
                    </Text>
                  )}
                </Flex>
              </StyledFilterOption>
            );
          })}
        </>
      }
      dropdownContentProps={{ padding: '0', ...dropdownProps }}
      filterName={
        showSelectedValuesChips
          ? `${filterName}${selectedValuesDisplay}`
          : filterName
      }
      size="small"
    />
  );
};

export default SelectFilter;
